<script setup lang="ts">
import {
  InteractionEventStatus,
  InteractionEventType,
  MailSenderStatus,
} from "@/enums/mission";
import { useMate } from "@/stores/mate";
import { InteractionEvent, MateMail } from "@/types/entity";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { MateMailType } from "@/enums/mateEnums";
import { useI18n } from "vue-i18n";
import { useInteractiveReview } from "@/stores/interactive-review";

const props = defineProps<{
  event: InteractionEvent;
  isNeedAction: boolean;
}>();

const emit = defineEmits<{
  (e: "edit-mail", interactionEvent: InteractionEvent): void;
}>();

const reviewStore = useInteractiveReview();
const mateStore = useMate();
const router = useRouter();
const { t } = useI18n();
const expanded = ref(false);

const entity = computed<MateMail>(() => {
  return props.event.entity! as MateMail;
});

const desc = computed<string>(() => {
  if (props.event.type === InteractionEventType.LetterSent) {
    return t("interactive.reviewPage.mail.mailSent");
  } else if (props.event.type === InteractionEventType.ReceivedLetter) {
    return t("interactive.reviewPage.mail.receivedMail");
  } else {
    return t("interactive.reviewPage.mail.requestSendMail");
  }
});

const rejectText = computed<string>(() => {
  return t("interactive.reviewPage.mail.userRejected", {
    name: props.event.mate!.name,
    targetName: entity.value!.recipient?.name,
  });
});
const approveText = computed<string>(() => {
  return t("interactive.reviewPage.mail.userApproved", {
    name: props.event.mate!.name,
    targetName: entity.value!.recipient?.name,
  });
});

const viewType = computed<MateMailType>(() => {
  if (entity.value!.senderStatus === MailSenderStatus.Trashed) {
    return MateMailType.Trashed;
  }
  if (mateStore.isMyMateById(entity.value!.senderId)) {
    if (entity.value!.senderStatus === MailSenderStatus.Sent) {
      return MateMailType.Sent;
    } else {
      return MateMailType.Draft;
    }
  } else {
    return MateMailType.Inbox;
  }
});

const showMailHasReplied = computed<boolean>(() => {
  if (props.event.type !== InteractionEventType.ReceivedLetter) return false;
  return (
    entity.value.children?.some(
      (child) => child.senderId === props.event.mateId
    ) ?? false
  );
});

const clickViewMailList = () => {
  let { senderId, sender, recipientId, recipient } = entity.value!;
  let name = recipient?.name;
  if (mateStore.isMyMateById(recipientId)) {
    [senderId, recipientId] = [recipientId, senderId];
    name = sender?.name;
  }
  router.push({
    name: "mailList",
    query: {
      type: MateMailType.Threadbox,
      sourceId: senderId,
      targetId: recipientId,
      targetName: name,
    },
  });
};
</script>
<template>
  <InteractionItemHeader
    :mate="event.mate"
    :title="event.title"
    :desc="desc"
    icon="iconify mdi--email"
    :dtCreate="event.dtCreate"
    :is-need-action="isNeedAction"
  />
  <div
    :class="{
      'text-primary-content': isNeedAction,
      'text-base-content': !isNeedAction,
    }"
  >
    {{ event.content }}
  </div>
  <div
    v-if="entity.subject"
    class="flex flex-col gap-1"
    :class="{
      'text-primary-content/50': isNeedAction,
      'text-base-content/50': !isNeedAction,
    }"
  >
    <div>
      {{ t("interactive.reviewPage.mail.subject") }}: {{ entity.subject }}
    </div>
    <div class="flex items-center justify-between">
      <div
        v-if="event.type !== InteractionEventType.ReceivedLetter"
        class="flex items-center gap-2"
      >
        {{ t("interactive.reviewPage.mail.reciver") }}:
        <RouterLink
          v-if="entity.recipient"
          class="text-accent hover:underline hover:text-accent"
          :to="{
            name: 'mateHome',
            params: {
              uuid: entity.recipient?.uuid || '',
            },
          }"
        >
          {{ entity.recipient?.name }}
        </RouterLink>
        <div v-else class="text-error hover:underline hover:text-error">
          该角色状态异常
        </div>
      </div>
      <div v-else class="flex items-center gap-2">
        {{ t("interactive.reviewPage.mail.sender") }}:
        <RouterLink
          v-if="entity.sender"
          class="text-accent hover:underline hover:text-accent"
          :to="{
            name: 'mateHome',
            params: {
              uuid: entity.sender?.uuid,
            },
          }"
        >
          {{ entity.sender?.name }}
        </RouterLink>
        <div v-else class="text-error hover:underline hover:text-error">
          该角色状态异常
        </div>
      </div>
      <button
        class="btn-ghost underline cursor-pointer"
        @click="expanded = !expanded"
      >
        {{
          expanded
            ? t("interactive.reviewPage.mail.collapse")
            : t("interactive.reviewPage.mail.expand")
        }}
      </button>
    </div>
    <div
      class="prose prose-sm mail-preview"
      v-html="entity.body"
      :class="{
        'line-clamp-3': !expanded,
        'line-clamp-none': expanded,
        'text-primary-content/50': isNeedAction,
        'text-base-content/50': !isNeedAction,
      }"
    />
  </div>
  <div
    v-if="event.reviewStatus === InteractionEventStatus.Pending"
    class="flex flex-col gap-2"
  >
    <div class="flex gap-2">
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="
          reviewStore.reviewEvent(event.id, InteractionEventStatus.Approved)
        "
      >
        {{ t("interactive.reviewPage.mail.agreeAndSend") }}
      </button>
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="emit('edit-mail', event)"
      >
        {{ t("game.modify") }}
      </button>
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="
          reviewStore.reviewEvent(event.id, InteractionEventStatus.Rejected)
        "
      >
        {{ t("Buttons.deny") }}
      </button>
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="clickViewMailList"
      >
        {{ t("interactive.reviewPage.mail.viewRelatedMails") }}
      </button>
    </div>
  </div>
  <div v-else class="flex flex-col gap-2">
    <div
      v-if="event.reviewStatus === InteractionEventStatus.Rejected"
      class="text-error"
    >
      {{ rejectText }}
    </div>
    <div
      v-if="event.reviewStatus === InteractionEventStatus.Approved"
      class="text-success"
    >
      {{ approveText }}
    </div>
    <template
      v-if="
        event.type === InteractionEventType.ReceivedLetter && entity.children
      "
    >
      <div v-if="showMailHasReplied" class="text-success">
        {{
          t("interactive.reviewPage.mail.mailHasReplied", {
            name: event.mate!.name,
            targetName: entity.sender?.name,
          })
        }}
      </div>
      <div v-else class="text-warning">
        {{
          t("interactive.reviewPage.mail.mailHasNotReplied", {
            name: event.mate!.name,
            targetName: entity.sender?.name,
          })
        }}
      </div>
    </template>
    <div
      v-if="
        entity &&
        entity.senderStatus !== MailSenderStatus.Deleted &&
        event.reviewStatus !== InteractionEventStatus.Rejected
      "
      class="flex gap-2 items-center"
    >
      <button
        class="btn btn-xs rounded w-fit"
        v-if="entity.uuid"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="
          router.push({
            name: 'mailDetail',
            params: {
              uuid: entity.uuid,
            },
            query: {
              type: viewType,
            },
          })
        "
      >
        {{ t("interactive.reviewPage.goToView") }}
      </button>
      <!-- <button
        class="btn btn-xs rounded btn-outline"
        @click="clickViewMailList()"
      >
        查看往来信件
      </button> -->
    </div>
  </div>
</template>
<style>
.mail-preview.prose.prose-sm p {
  margin: 0;
}
</style>
